import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Activity, ChevronRight, DollarSign, Layers } from "./overview/icons";
import {
  CostReductionCard,
  KpiSkeleton,
  StatCard,
  SubStat,
  TokenBreakdown,
} from "./overview/KpiCards";
import { SAVINGS_HINT, SPEND_HINT } from "./overview/metricHints";
import { OptimizationIcon } from "./agent/optimizationIcons";
import { ConnectedPersonalizationLevel } from "./agent/PersonalizationLevel";
import { PERSONALIZATION_LEVEL_FLAG, useFlags } from "../services/flags";
import { Tooltip } from "../design-system/components/Tooltip/Tooltip";
import {
  AgentKindLogo,
  editDisabledReason,
  kindSpec,
  useAgent,
  useAgentClient,
  useAgentOptimizations,
  type Agent,
  type AgentKind,
  type AgentOptimization,
} from "../agents";
import {
  AgentScopeProvider,
  formatCompact,
  formatPct,
  formatUSD,
  friendlyError,
  invalidateQueries,
  savedPct,
  toApiError,
  useAgentRollups,
  type AgentRollup,
} from "../analytics";
import {
  trackAgentCreated,
  trackApiFailed,
  trackButtonPressed,
  trackInsightActionPressed,
  trackInsightShown,
  type InsightRef,
} from "../analytics/tracking";
import { useOnVisible } from "../analytics/useOnVisible";
import {
  insightKind,
  useInsightClient,
  useInsights,
  type FeedbackVerdict,
  type Insight,
  type InsightState,
} from "../insights";
import { workspaceKeyPrefix } from "../identity/active";
import { useWsPath } from "../workspaces";
import { ReconnectDrawer } from "../components/layout/ReconnectDrawer";
import { FailuresPanel } from "./failures/FailuresPanel";
import {
  SAFETY_LABEL,
  allGroups,
  applyExclusivity,
  defaultValues,
  overridesFromValues,
  transformerByKey,
  useConfig,
  useConfigClient,
  useConfigSchema,
  valuesFromOverrides,
  FAMILIES,
  type ConfigValues,
  type Safety,
} from "../configs";

/* Per-agent detail view (OPSS-137), restyled to the Gateway-App Figma agent
 * page (nodes 284:3227 light / 284:3726 dark): identity band with the kind
 * tile and a "…" actions menu, the overview's KPI card row fed from this
 * agent's rollup, the savings-recommendations rail, and the full-bleed
 * "Agent configuration" block (family nav + optimization card grid).
 *
 * KPIs + identity are wired to real per-agent data (AgentScopeProvider pins the
 * analytics reads to this agent_id). The optimizations grid is driven by the
 * per-agent discovery endpoint (useAgentOptimizations, OPSS-152); toggles
 * persist to the agent's bound config profile via a debounced write. The
 * savings-recommendations rail (Figma 537:540) is fed by the insights read
 * API (useInsights, OPSS-206/209/210): published insights render as
 * "Optimization found" cards whose Apply/Dismiss call the lifecycle transitions
 * — or, for advisory insights, a "Recommended action" card whose 👍/👎 do
 * (OPSS-283). */
export default function AgentDetailPage() {
  const { agentId = "" } = useParams();

  // Pin every analytics read on this page to the selected agent.
  return (
    <AgentScopeProvider agentId={agentId || null}>
      <AgentDetailBody agentId={agentId} />
    </AgentScopeProvider>
  );
}

function AgentDetailBody({ agentId }: { agentId: string }) {
  const agentClient = useAgentClient();
  const navigate = useNavigate();
  const ws = useWsPath();

  // Identity: prefer the registered agent (name/kind/description), fall back to
  // the rollup row (a tagged-but-unregistered agent_id has no /settings entry).
  // Bumping `version` re-keys useAgent so an in-place edit shows without reload.
  const [version, setVersion] = useState(0);
  const agent = useAgent(agentId, version);

  // The post-save version bump re-keys useAgent, whose data clears while the
  // re-read is in flight — without a hold, the header would fall back to the
  // rollup's stale name (or the raw agent id) for the duration and the old
  // identity would pop back in. Keep the last good record per agent, and
  // overlay the identity we just wrote (`pending`) until the re-read lands.
  const heldRef = useRef<{ id: string; record: Agent | null }>({
    id: agentId,
    record: null,
  });
  if (heldRef.current.id !== agentId) {
    heldRef.current = { id: agentId, record: null };
  }

  if (agent.data !== undefined) heldRef.current.record = agent.data;

  const [pending, setPending] = useState<{
    name: string;
    kind: AgentKind;
    description: string | null;
  } | null>(null);
  useEffect(() => {
    // Retire the overlay only once the server record reflects the write —
    // right after a save this effect still sees the pre-save record (the
    // re-keyed read hasn't cleared it yet), and dropping the overlay then
    // would pop the old identity back in.
    const d = agent.data;
    if (
      pending &&
      d !== undefined &&
      d.name === pending.name &&
      d.kind === pending.kind &&
      (d.description ?? null) === pending.description
    ) {
      setPending(null);
    }
  }, [pending, agent.data]);

  const record = agent.data ?? heldRef.current.record ?? undefined;

  // Per-agent KPIs come from THIS agent's row in the /agents rollup — the only
  // per-agent cut the v1 contract exposes. (/summary is workspace-global: it
  // ignores agent_id, so it would show the same numbers as the Overview.)
  const rollups = useAgentRollups();
  const rollup = rollups.data?.data.items.find((a) => a.agent_id === agentId);

  // Hold the meter until the flag read settles, so it can't flash in and out.
  const flags = useFlags();
  const personalizationOn =
    flags.ready && flags.isEnabled(PERSONALIZATION_LEVEL_FLAG);

  const registered = record !== undefined;
  // A traffic-only agent: telemetry rows exist (it's in the rollup) but
  // /settings/agents has no record, so the GET 404s. Distinct from `!registered`
  // while the read is still in flight (error undefined) — only a confirmed 404
  // offers the setup path.
  const unregistered =
    !registered && toApiError(agent.error)?.code === "not_found";
  const name = pending?.name ?? record?.name ?? rollup?.name ?? agentId;
  const kind = (pending?.kind ??
    record?.kind ??
    rollup?.agent_kind ??
    "") as string;
  const description = pending
    ? pending.description
    : (record?.description ?? null);
  const configId = record?.config_id ?? null;
  // Server-computed (permissions.py can_edit_agent): admin, or the recorded
  // creator, or enforcement is off entirely. Absent while unregistered/loading
  // — default true so those states read as editable until proven otherwise,
  // matching what registerAgent below actually allows (creation is open to any
  // signed-in member).
  const canEdit = record?.can_edit ?? true;

  const [reconnectOpen, setReconnectOpen] = useState(false);
  // Owned here rather than in AgentModelBreakdown: that section is mounted
  // behind rollups.data, which a Refresh / timeframe change momentarily clears.
  const [modelsOpen, setModelsOpen] = useState(false);
  // The failure drill-down. Rendered under this page's AgentScopeProvider, so
  // useScopedQuery pins both of its reads to this agent with no extra plumbing.
  const [failuresOpen, setFailuresOpen] = useState(false);

  const refreshAgent = () => {
    setVersion((v) => v + 1);
    invalidateQueries(`${workspaceKeyPrefix()}agents`);
  };

  // An applied insight rewrote the agent's bound config profile server-side:
  // drop the cached profile read and remount the configuration block (epoch in
  // its key) so it reseeds from the updated overrides instead of local state.
  const [configEpoch, setConfigEpoch] = useState(0);
  const onInsightApplied = () => {
    if (configId) {
      invalidateQueries(`${workspaceKeyPrefix()}config:${configId}`);
    }

    setConfigEpoch((e) => e + 1);
  };

  // Auto-advance the onboarding state while the reconnect drawer is open: poll
  // the agent until `used` flips (it latches immediately on the first
  // x-agent-id request), so a just-connected agent reflects "connected" without
  // a manual reload. Stops on the flip, on close, and on unmount. Bumps the
  // version directly rather than through refreshAgent so the interval isn't
  // reset by refreshAgent's per-render identity.
  const used = agent.data?.used === true;
  useEffect(() => {
    if (!reconnectOpen || used) return;

    const id = setInterval(() => {
      setVersion((v) => v + 1);
      invalidateQueries(`${workspaceKeyPrefix()}agents`);
    }, 5000);
    return () => clearInterval(id);
  }, [reconnectOpen, used]);

  const save = async (next: {
    name: string;
    kind: AgentKind;
    description: string | null;
  }) => {
    // Optimistic: show the new values the moment the field closes — waiting
    // for the request would flash the old identity for its duration. A failed
    // write reverts the overlay (and the caller surfaces the error).
    setPending(next);
    try {
      await agentClient.update(agentId, {
        name: next.name,
        kind: next.kind,
        description: next.description,
      });
    } catch (err) {
      setPending(null);
      throw err;
    }
    // Refresh this page's agent read and the shell breadcrumb/rollups.
    refreshAgent();
  };

  // Register a traffic-discovered agent, seeded from the identity already on
  // screen (the rollup's name/kind; kindSpec maps an unknown telemetry kind to
  // "other"). Registration is an explicit user action — auto-register-on-read
  // was rejected. The backend creates + binds a default config profile in the
  // same transaction, so after the re-read the page is fully editable. A lost
  // race with a concurrent registration counts as success.
  const registerAgent = async () => {
    try {
      await agentClient.create({
        agent_id: agentId,
        kind: kindSpec(kind).value,
        name: name || agentId,
      });
    } catch (err) {
      const existing = await agentClient.get(agentId).catch(() => null);
      if (!existing) throw err;

      // create() threw, so the client decorator never fired AgentCreated — but
      // the agent exists and we report success to the user. Fire it here so the
      // event isn't lost (it is also a marketing conversion, OPSS-249).
      trackAgentCreated(existing.agent_id, existing.kind);
    }
    refreshAgent();
  };

  // Permanent delete (the client fires AgentDeleted analytics). The page's
  // subject is gone afterwards, so leave for the workspace overview. Drop the
  // ENTIRE workspace cache, not just the `agents` prefix: the overview roster
  // comes from the analytics rollups, whose key starts with the filter-query
  // JSON — a prefix invalidation on "agents" never matches it.
  const removeAgent = async () => {
    await agentClient.remove(agentId);
    invalidateQueries(workspaceKeyPrefix());
    navigate(ws("/"), { replace: true });
  };

  return (
    <div className="flex flex-col">
      <AgentHeader
        name={name}
        kind={kind}
        description={description}
        editable={registered}
        canEdit={canEdit}
        onSave={save}
        onDelete={removeAgent}
        onReconnect={() => setReconnectOpen(true)}
        personalization={
          personalizationOn ? (
            <ConnectedPersonalizationLevel agentId={agentId} />
          ) : null
        }
      />

      <div className="mt-4">
        {rollups.data ? (
          <AgentKpiRow
            rollup={rollup}
            onOpenFailures={() => {
              trackButtonPressed("Failed requests", "agent", agentId);
              setFailuresOpen(true);
            }}
          />
        ) : (
          <KpiSkeleton />
        )}
      </div>

      {/* Which models this agent's spend went to (OPSS-421). Reads the agent's
       * own rollup row, so it is agent-scoped on every backend read path and
       * costs no extra request. */}
      {rollups.data && (rollup?.models.length ?? 0) > 0 && (
        <div className="mt-5">
          <AgentModelBreakdown
            rollup={rollup}
            open={modelsOpen}
            onToggle={() => setModelsOpen((o) => !o)}
          />
        </div>
      )}

      {/* Insights only make sense once the agent has sent traffic (`used`
       * latches on the first data-plane request) — omit the rail entirely
       * for silent agents. */}
      {registered && used && (
        <Recommendations agentId={agentId} onApplied={onInsightApplied} />
      )}

      {unregistered ? (
        <RegisterAgentPanel onRegister={registerAgent} />
      ) : (
        // Keyed by the bound config so a config_id arriving after mount (the
        // agent read landing, or registration binding the default profile)
        // remounts and reseeds from that profile — otherwise the block keeps
        // schema defaults and a stale null boundIdRef.
        <AgentConfiguration
          key={`${configId ?? "unbound"}:${configEpoch}`}
          agentId={agentId}
          configId={configId}
          agentName={name}
          canEdit={canEdit}
          onBind={refreshAgent}
        />
      )}

      <ReconnectDrawer
        open={reconnectOpen}
        onClose={() => setReconnectOpen(false)}
        agentId={agentId}
        kind={(kind || "other") as AgentKind}
      />

      {/* No onOpenAgent: every row here is this agent already. */}
      <FailuresPanel
        open={failuresOpen}
        onClose={() => setFailuresOpen(false)}
      />
    </div>
  );
}

// ── Agent header ─────────────────────────────────────────────────────────────

/* Identity band (Figma "Container" 284:3248): the 67px kind tile, the name
 * with the (read-only) kind label to its right and the description underneath,
 * and the "…" actions menu on the far right. Name and description edit in
 * place: they grow a hairline border on hover and turn into fields on click.
 * Each commit persists the full identity via onSave — the kind is carried
 * along unchanged; a failed write reverts (the display always reads the
 * props). */
function AgentHeader({
  name,
  kind,
  description,
  editable,
  canEdit,
  onSave,
  onDelete,
  onReconnect,
  personalization,
}: {
  name: string;
  kind: string;
  description: string | null;
  /** Only a registered agent (has a /settings entry) can be edited. */
  editable: boolean;
  /** Whether the signed-in caller may write to this (registered) agent —
   *  `Agent.can_edit`. Irrelevant while unregistered (nothing to own yet). */
  canEdit: boolean;
  onSave: (next: {
    name: string;
    kind: AgentKind;
    description: string | null;
  }) => Promise<void>;
  /** Permanently deletes the agent; the caller navigates away on success. */
  onDelete: () => Promise<void>;
  onReconnect: () => void;
  /** Personalization meter slot. Null when the flag is off — passed as a node
   *  rather than a level so the meter's reads only happen when it's mounted. */
  personalization: ReactNode;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Only meaningful once registered — an unregistered agent has no owner
  // verdict to reflect, and `editable` already keeps its fields non-interactive.
  const disabledReason = editable
    ? editDisabledReason({ can_edit: canEdit })
    : undefined;

  // Persist one changed field, carrying the rest of the identity along
  // (the agents API takes the full {name, kind, description} payload).
  const save = async (
    patch: Partial<{
      name: string;
      kind: AgentKind;
      description: string | null;
    }>,
  ) => {
    if (busy) return;

    setBusy(true);
    setError(null);
    try {
      await onSave({
        name,
        kind: (kind || "other") as AgentKind,
        description,
        ...patch,
      });
    } catch (err) {
      // friendlyError's "forbidden" copy is analytics-specific ("switch
      // workspaces or ask an admin") — wrong here, since this 403 means the
      // caller isn't this agent's creator/admin, not that they lack workspace
      // access. The backend's own detail already says that correctly.
      const api = toApiError(err);
      setError(
        api?.code === "forbidden" ? api.message : friendlyError(err).message,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    // z-10: the float-up animations give each page block its own stacking
    // context, so without a raise the "…" menu paints under the KPI cards.
    <div className="tw-float-up relative z-10 flex min-h-[96px] items-center gap-7 py-3">
      <AgentKindLogo kind={kind} className="size-[67px] shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3">
          <InlineName
            value={name}
            editable={editable && !busy}
            disabledReason={disabledReason}
            onCommit={(next) => save({ name: next })}
          />
          {/* The kind is read-only here — it's set at connect time and carried
              along unchanged by identity saves. */}
          <span
            className="shrink-0 rounded-[5px] px-2 py-1 text-[11px] leading-none"
            style={{
              background: "var(--surface-2)",
              color: "var(--text-muted)",
            }}
          >
            {kindSpec(kind).label}
          </span>
        </div>
        <InlineDescription
          value={description}
          editable={editable && !busy}
          disabledReason={disabledReason}
          onCommit={(next) => save({ description: next })}
        />
        {error && (
          <p
            className="mt-1 text-[11px]"
            style={{ color: "var(--val-crimson)" }}
          >
            {error}
          </p>
        )}
      </div>
      {/* At xl the right half spans the KPI grid's last two columns — four equal
          columns with a 16px gap means columns 3+4 are (100% − gap)/2 — so the
          meter starts on the Sessions card's left edge. It does NOT stretch:
          the meter sizes itself to its caption (w-max there), which is what
          makes the bar and the sentence the same length, and ml-auto holds "…"
          flush right, leaving the slack between them as in the design. Below xl
          the cards reflow to 2/1 columns, so there's nothing to align to and
          only the "…" remains (see the meter's own breakpoint). */}
      <div className="flex shrink-0 items-center gap-4 xl:w-[calc(50%-8px)]">
        {personalization}
        <div className="ml-auto shrink-0">
          <AgentActionsMenu
            onReconnect={onReconnect}
            onDelete={onDelete}
            deleteDisabledReason={disabledReason}
          />
        </div>
      </div>
    </div>
  );
}

/* Shared look for the hover-to-reveal editable affordance — see
 * .tw-inline-edit in app-theme.css (a dedicated class so the dark theme's
 * `.border` recolor can't paint the hairline while idle). */
const INLINE_HIT = "tw-inline-edit -mx-1.5 px-1.5 text-left";

/* The agent name — a heading until clicked, then an input in the same type.
 * Enter/blur commit (unchanged or empty drafts just close), Escape cancels. */
function InlineName({
  value,
  editable,
  disabledReason,
  onCommit,
}: {
  value: string;
  editable: boolean;
  /** Set when `editable` but the caller may not write — shown on hover
   *  instead of the rename affordance. */
  disabledReason?: string;
  onCommit: (next: string) => Promise<void>;
}) {
  // null = display mode; a string is the in-progress draft.
  const [draft, setDraft] = useState<string | null>(null);
  const cancelled = useRef(false);

  const heading = (
    <h2
      className="text-[20px] font-semibold leading-[1.55]"
      style={{ color: "var(--text)" }}
    >
      {value}
    </h2>
  );

  if (!editable) return heading;

  if (disabledReason) {
    return (
      <Tooltip content={disabledReason}>
        <span tabIndex={0} className="cursor-not-allowed">
          {heading}
        </span>
      </Tooltip>
    );
  }

  if (draft === null) {
    return (
      <button
        onClick={() => {
          cancelled.current = false;
          setDraft(value);
        }}
        aria-label="Rename agent"
        title="Rename"
        className={INLINE_HIT}
      >
        {heading}
      </button>
    );
  }

  const commit = () => {
    const next = draft.trim();
    setDraft(null);
    if (cancelled.current || !next || next === value) return;

    onCommit(next);
  };

  return (
    <input
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();

        if (e.key === "Escape") {
          cancelled.current = true;
          e.currentTarget.blur();
        }
      }}
      aria-label="Agent name"
      className="-mx-1.5 w-[280px] rounded-[5px] bg-transparent px-1.5 text-[20px] font-semibold leading-[1.55] focus:outline-none"
      style={{ color: "var(--text)", border: "1px solid var(--border-2)" }}
    />
  );
}

/* The description line under the name — always reserves its slot when
 * editable, showing a muted placeholder until one is set. Clears back to null
 * on an empty commit. */
function InlineDescription({
  value,
  editable,
  disabledReason,
  onCommit,
}: {
  value: string | null;
  editable: boolean;
  /** Set when `editable` but the caller may not write — shown on hover
   *  instead of the edit affordance. */
  disabledReason?: string;
  onCommit: (next: string | null) => Promise<void>;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const cancelled = useRef(false);

  if (!editable) {
    return value ? (
      <p
        className="max-w-[430px] text-[11px] leading-[1.6]"
        style={{ color: "var(--text-muted)" }}
      >
        {value}
      </p>
    ) : null;
  }

  if (disabledReason) {
    return (
      <Tooltip content={disabledReason}>
        <span tabIndex={0} className="block max-w-[430px] cursor-not-allowed">
          <p
            className="text-[11px] leading-[1.6]"
            style={{ color: "var(--text-muted)", opacity: value ? 1 : 0.6 }}
          >
            {value ?? "Add a description…"}
          </p>
        </span>
      </Tooltip>
    );
  }

  if (draft === null) {
    return (
      <button
        onClick={() => {
          cancelled.current = false;
          setDraft(value ?? "");
        }}
        aria-label="Edit description"
        title="Edit description"
        className={`block max-w-[430px] ${INLINE_HIT}`}
      >
        <p
          className="text-[11px] leading-[1.6]"
          style={{
            color: "var(--text-muted)",
            opacity: value ? 1 : 0.6,
          }}
        >
          {value ?? "Add a description…"}
        </p>
      </button>
    );
  }

  const commit = () => {
    const next = draft.trim() || null;
    setDraft(null);
    if (cancelled.current || next === (value ?? null)) return;

    onCommit(next);
  };

  return (
    <input
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();

        if (e.key === "Escape") {
          cancelled.current = true;
          e.currentTarget.blur();
        }
      }}
      aria-label="Agent description"
      placeholder="What this agent does"
      className="-mx-1.5 block w-[430px] rounded-[5px] bg-transparent px-1.5 text-[11px] leading-[1.6] focus:outline-none"
      style={{ color: "var(--text)", border: "1px solid var(--border-2)" }}
    />
  );
}

/* The header's "…" overflow menu (Figma Button 284:3748): Reconnect and a
 * two-step Delete (identity edits happen inline in the header). Closes on
 * outside click. */
function AgentActionsMenu({
  onReconnect,
  onDelete,
  deleteDisabledReason,
}: {
  onReconnect: () => void;
  onDelete: () => Promise<void>;
  /** Set when the caller may not write to this agent — disables Delete and
   *  shows this on hover instead of opening the confirm step. */
  deleteDisabledReason?: string;
}) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const onDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;

      setOpen(false);
      setConfirming(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const item =
    "flex h-[38px] w-full items-center px-4 text-left text-[13px] transition-colors hover:bg-[var(--hover)]";

  const submitDelete = async () => {
    if (deleting) return;

    setDeleting(true);
    setDeleteError(null);
    try {
      await onDelete();
      // Success navigates away and unmounts the menu — nothing to reset.
    } catch (err) {
      // Same reasoning as AgentHeader's save(): a 403 here means "not this
      // agent's creator/admin", not the generic analytics-access copy.
      const api = toApiError(err);
      setDeleteError(
        api?.code === "forbidden" ? api.message : friendlyError(err).message,
      );
      setDeleting(false);
    }
  };

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Agent actions"
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex size-8 items-center justify-center rounded-[5px] transition-colors hover:bg-[var(--hover)]"
        style={{ color: "var(--text-muted)" }}
      >
        <svg width="17" height="17" viewBox="0 0 17 17" fill="currentColor">
          <circle cx="3.5" cy="8.5" r="1.4" />
          <circle cx="8.5" cy="8.5" r="1.4" />
          <circle cx="13.5" cy="8.5" r="1.4" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-9 z-20 w-[190px] overflow-hidden rounded-[5px] py-1 shadow-lg"
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            color: "var(--text)",
          }}
        >
          <button
            role="menuitem"
            className={item}
            onClick={() => {
              trackButtonPressed("Reconnect", "agent");
              setOpen(false);
              onReconnect();
            }}
          >
            Reconnect
          </button>
          {confirming ? (
            <div style={{ borderTop: "1px solid var(--border)" }}>
              <div className="flex h-[38px] items-center justify-between gap-2 px-4">
                <button
                  role="menuitem"
                  onClick={() => {
                    trackButtonPressed("Confirm delete agent", "agent");
                    submitDelete();
                  }}
                  disabled={deleting}
                  className="text-[13px] font-semibold disabled:opacity-50"
                  style={{ color: "var(--val-crimson)" }}
                >
                  {deleting ? "Deleting…" : "Confirm delete"}
                </button>
                <button
                  role="menuitem"
                  onClick={() => {
                    setConfirming(false);
                    setDeleteError(null);
                  }}
                  disabled={deleting}
                  className="text-[13px]"
                  style={{ color: "var(--text-muted)" }}
                >
                  Keep
                </button>
              </div>
              {deleteError && (
                <p
                  className="px-4 pb-2 text-[11px]"
                  style={{ color: "var(--val-crimson)" }}
                >
                  {deleteError}
                </p>
              )}
            </div>
          ) : deleteDisabledReason ? (
            // A native title rather than the styled Tooltip: this button is a
            // flex item sized to fill the menu row (w-full via `item`), and
            // Tooltip's inline-flex wrapper would fight that sizing.
            <button
              role="menuitem"
              disabled
              title={deleteDisabledReason}
              className={`${item} cursor-not-allowed opacity-50`}
              style={{ color: "var(--val-crimson)" }}
            >
              Delete agent
            </button>
          ) : (
            <button
              role="menuitem"
              className={item}
              style={{ color: "var(--val-crimson)" }}
              onClick={() => {
                trackButtonPressed("Delete agent", "agent");
                setConfirming(true);
              }}
            >
              Delete agent
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── KPIs (per-agent, from this agent's rollup row) ───────────────────────────

/* KPIs are sourced from this agent's rollup row (AgentRollup), not the global
 * /summary. `rollup` is undefined when the agent has no telemetry in the window
 * (a registered-but-idle agent, or a stale route) — every card then degrades to
 * zeros rather than crashing. The mint card uses the same $ basis as the overview
 * headline (OPSS-242), so the two never disagree. */
/* Per-model breakdown of one agent's usage (OPSS-421) — the agent-scoped half of
 * "usage per agent and per model".
 *
 * Sourced from the agent's own rollup row rather than a `/models?agent_id=` read:
 * the rows behind it are grouped by (agent, model) on every backend read path, so
 * they are correctly scoped, already fetched, and guaranteed to sum to the Spend
 * tile directly above.
 *
 * `cost_usd`/`tokens`/`savings_usd` are optional in the contract (an older BFF
 * sends only `{name, calls}`), so each is `?? 0` — never `||`, since a genuine 0
 * must win. A share bar makes the mix readable at a glance; sorted by spend so the
 * row that explains the bill is first.
 *
 * Collapsed by default: the page's job is the agent, and most visits do not need
 * the model split — it is a detail you open, not one you scroll past.
 *
 * `open` is owned by the PAGE, not by this component: the section is mounted
 * behind `rollups.data`, and a Refresh or a timeframe change re-keys the query
 * so that data briefly goes undefined. Local state would unmount with it and the
 * section would silently re-collapse on every refresh — worst in exactly the
 * compare-across-timeframes flow this section is for. */
function AgentModelBreakdown({
  rollup,
  open,
  onToggle,
}: {
  rollup: AgentRollup | undefined;
  open: boolean;
  onToggle: () => void;
}) {
  const models = [...(rollup?.models ?? [])].sort(
    (a, b) => (b.cost_usd ?? 0) - (a.cost_usd ?? 0),
  );
  const total = models.reduce((sum, m) => sum + (m.cost_usd ?? 0), 0);

  return (
    <section>
      {/* Hover feedback is opacity, not colour: the sibling section titles set
          their colour inline, and an inline style outranks a Tailwind
          hover:text-* class — so the usual hover-to-`--text` pattern would be
          dead here (and is a no-op anyway when the base colour is already
          `--text`). */}
      <button
        onClick={onToggle}
        aria-expanded={open}
        className="mb-3 flex items-center gap-1.5 text-[15px] leading-none transition-opacity hover:opacity-70"
        style={{ color: "var(--text)" }}
      >
        Usage by model{" "}
        <span style={{ color: "var(--text-muted)" }}>({models.length})</span>
        <ChevronRight
          size={14}
          className={`transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>
      {open && (
        <div className="flex flex-col gap-2">
          {models.map((m) => {
            const cost = m.cost_usd ?? 0;
            const saved = m.savings_usd ?? 0;
            const tokens = m.tokens
              ? m.tokens.input +
                m.tokens.output +
                m.tokens.cache_read +
                m.tokens.cache_write
              : 0;
            // Share of this agent's spend. Falls back to call share when the
            // window is entirely unpriced, so the bar still says something.
            const share =
              total > 0
                ? cost / total
                : rollup?.call_count
                  ? m.calls / rollup.call_count
                  : 0;
            return (
              <div
                key={m.name}
                className="grid grid-cols-[minmax(140px,1.4fr)_minmax(0,1fr)_72px_72px_110px] items-center gap-4 rounded-[5px] border border-[var(--border)] px-5 py-[13px]"
              >
                <p
                  className="truncate text-[13px] font-medium leading-tight"
                  style={{ color: "var(--text)" }}
                  title={m.name}
                >
                  {m.name}
                </p>
                <div
                  className="h-1.5 w-full overflow-hidden rounded-full"
                  style={{ background: "var(--surface-2)" }}
                  role="img"
                  aria-label={`${Math.round(share * 100)}% of this agent's spend`}
                >
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.max(share * 100, 1)}%`,
                      background: "var(--text-muted)",
                    }}
                  />
                </div>
                <div className="flex flex-col items-end gap-0.5">
                  <span
                    className="tw-mono text-[11px] font-medium leading-none tabular-nums"
                    style={{ color: "var(--text)" }}
                  >
                    {formatUSD(cost)}
                  </span>
                  <span
                    className="tw-mono text-[10px] uppercase leading-none tracking-[0.25px]"
                    style={{ color: "var(--text-muted)" }}
                  >
                    Spend
                  </span>
                </div>
                <div className="flex flex-col items-end gap-0.5">
                  <span
                    className="tw-mono text-[11px] font-medium leading-none tabular-nums"
                    style={{ color: "var(--text)" }}
                  >
                    {formatCompact(tokens || m.calls)}
                  </span>
                  <span
                    className="tw-mono text-[10px] uppercase leading-none tracking-[0.25px]"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {tokens ? "Tokens" : "Calls"}
                  </span>
                </div>
                {/* Same saved-% pill as the overview's Models rows, on the same
                  basis, so a model reads identically in both places. */}
                {saved > 0 ? (
                  <span
                    className="tw-mono justify-self-end rounded-full px-2.5 py-1 text-[10px] font-medium uppercase leading-none tracking-[0.25px]"
                    style={{
                      background: "var(--savings-card-bg)",
                      color: "var(--savings-fg)",
                    }}
                  >
                    <span className="tabular-nums">
                      {formatPct(savedPct(saved, cost))}
                    </span>{" "}
                    saved
                  </span>
                ) : (
                  <span aria-hidden />
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function AgentKpiRow({
  rollup,
  onOpenFailures,
}: {
  rollup: AgentRollup | undefined;
  onOpenFailures: () => void;
}) {
  const cost = rollup?.cost.total ?? 0;
  const pctSaved = savedPct(rollup?.savings_usd ?? 0, cost);
  const callCount = rollup?.call_count ?? 0;
  // Failures consume no tokens, so they inflate callCount without moving spend.
  const failedCalls = rollup?.failed_calls ?? 0;
  const sessionCount = rollup?.session_count ?? 0;
  const modelCount = rollup?.models.length ?? 0;

  const input = rollup?.tokens.input ?? 0;
  const output = rollup?.tokens.output ?? 0;
  const cacheTokens =
    (rollup?.tokens.cache_read ?? 0) + (rollup?.tokens.cache_write ?? 0);
  const totalTokens = input + output + cacheTokens;
  // A sub-slice of the prompt tokens above (not added to the total), OPSS-289.
  const imageTokens = rollup?.tokens.image_tokens ?? 0;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <CostReductionCard pct={pctSaved} hint={SAVINGS_HINT} />

      <StatCard
        label="Spend"
        Icon={DollarSign}
        value={formatUSD(cost)}
        floatDelay={0.06}
        hint={SPEND_HINT}
      >
        <SubStat
          val={formatCompact(callCount)}
          unit="requests"
          note={
            failedCalls > 0 ? `${formatCompact(failedCalls)} failed` : undefined
          }
          noteLabel="View failed requests"
          onNoteClick={onOpenFailures}
        />
      </StatCard>

      <StatCard
        label="Sessions"
        Icon={Activity}
        value={formatCompact(sessionCount)}
        floatDelay={0.12}
      >
        <SubStat
          val={formatCompact(modelCount)}
          unit={modelCount === 1 ? "model" : "models"}
        />
      </StatCard>

      <StatCard
        label="Tokens"
        Icon={Layers}
        value={formatCompact(totalTokens)}
        floatDelay={0.18}
      >
        <TokenBreakdown
          rows={[
            ["Input", formatCompact(input)],
            ["Output", formatCompact(output)],
            ["Cache", formatCompact(cacheTokens)],
            ...(imageTokens > 0
              ? ([["Images", formatCompact(imageTokens)]] as [string, string][])
              : []),
          ]}
        />
      </StatCard>
    </div>
  );
}

// ── Savings recommendations ──────────────────────────────────────────────────
// The design's insight rail (Figma 537:540 on the page; card states in
// 351:5286: found 537:592 / applied 351:5123 / empty 351:5287), fed by the
// insights read API (OPSS-206/209/210). Published insights render as actionable
// cards; Apply merges the insight's config suggestion into the agent's config
// (published → applied) and the card flips to the applied style, Dismiss removes
// it (published → dismissed). The list endpoint returns published rows only, so
// applied cards are session-local: they persist until the next page load.
//
// A dismissal is immediate for both kinds — the card leaves the rail on the
// click, not when the request resolves — and comes back with the error if the
// transition fails. Nothing on a dismissed card can be acted on afterwards, so
// there is nothing to wait for.
//
// An advisory insight (OPSS-283) has no config suggestion — nothing we can apply
// on the user's behalf — so its card carries no Apply/Dismiss pair, and the
// design gives it its own ground so the two asks don't read as one. Its 👍/👎 are
// the verdict AND the lifecycle exit (👍 = acknowledge, 👎 = dismiss), submitted
// on the click: one tap, no follow-up. 👍 flips the card to the same applied
// style a config_change Apply lands on; 👎 removes it, because a dismissal
// removes the insight for both kinds rather than leaving a terminal card behind.
// The free-text comment step that used to sit between the two was dropped as
// friction; the BFF still accepts a comment and `feedback_comment` still rides
// the wire, we just never send one. Feedback goes out before the transition
// because it is only legal while the insight is `published`.
//
// config_change cards get no thumbs: Apply and Dismiss already encode the
// verdict, so those rows keep a null feedback_verdict and the funnel infers it
// from the state.
//
// `kind` is absent from the wire until the api-server mirror ships, and
// insightKind() reads that as `config_change`, so every card keeps behaving
// exactly as it does today until then.

/** Family-chip label for an insight, via the optimization it suggests
 *  (optimization_id → discovery-list card → FAMILIES label). Null (no chip)
 *  when the insight names no optimization or the id is unknown. */
function insightCategory(
  insight: Insight,
  optimizations: AgentOptimization[] | undefined,
): string | null {
  if (!insight.optimization_id) return null;

  const opt = optimizations?.find((o) => o.id === insight.optimization_id);
  if (!opt) return null;

  return FAMILIES.find((f) => f.key === opt.category)?.label ?? opt.category;
}

/** Analytics dimensions for one insight (OPSS-208). `agent_id` comes off the
 *  insight rather than the page, so the event names the agent the suggestion is
 *  scoped to even if the two ever diverge; `kind` goes through `insightKind()`
 *  so it reports the card the user actually saw. */
function insightRef(insight: Insight): InsightRef {
  return {
    insightId: insight.id,
    agentId: insight.agent_id,
    kind: insightKind(insight),
  };
}

export function Recommendations({
  agentId,
  onApplied,
}: {
  agentId: string;
  onApplied: () => void;
}) {
  const client = useInsightClient();
  const [version, setVersion] = useState(0);
  const insights = useInsights(agentId, version);
  const optimizations = useAgentOptimizations(agentId);

  // Applied/acknowledged insights are session-local (the published-only list
  // drops them); hold the transition responses so the card flips style instead
  // of vanishing. The response's `state` picks the done card's copy.
  const [done, setDone] = useState<Insight[]>([]);
  // Dismissals drop out of the rail on the click rather than waiting for the
  // refetch to stop serving them, so the card never lingers behind the request.
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A failed rating is keyed to the card whose thumb produced it, so the
  // message sits under that card rather than in the rail-wide `error` line.
  const [rateError, setRateError] = useState<{
    id: string;
    message: string;
  } | null>(null);

  const published = (insights.data ?? []).filter(
    (i) => !done.some((d) => d.id === i.id) && !dismissed.includes(i.id),
  );
  const cardCount = published.length + done.length;

  const transition = async (
    insight: Insight,
    action: "apply" | "dismiss",
    buttonText: string,
  ) => {
    trackButtonPressed(buttonText, "insight", insight.id);
    trackInsightActionPressed(insightRef(insight), action);
    setBusyId(insight.id);
    setError(null);

    // A dismissal lands on the click: the card leaves the rail before the
    // request resolves, and comes back — with the error — only if it fails.
    if (action === "dismiss") setDismissed((prev) => [...prev, insight.id]);

    try {
      if (action === "apply") {
        // Apply flips the card to the done style.
        const row = await client.apply(insight.id);
        setDone((prev) => [...prev.filter((d) => d.id !== row.id), row]);
        // Only apply rewrote the agent's bound config profile server-side.
        onApplied();
      } else {
        await client.dismiss(insight.id);
      }

      setVersion((v) => v + 1);
    } catch (err) {
      if (action === "dismiss") {
        setDismissed((prev) => prev.filter((id) => id !== insight.id));
      }

      setError(friendlyError(err).message);
    } finally {
      setBusyId(null);
    }
  };

  /** Advisory rating: record the verdict, then exit the lifecycle it implies.
   *  Feedback goes FIRST because it is only legal in `published` — if either
   *  call fails the insight stays in the rail with the error on its card, so
   *  the user can retry with another click. */
  const rate = async (insight: Insight, verdict: FeedbackVerdict) => {
    trackButtonPressed(
      verdict === "helpful" ? "Helpful" : "Not helpful",
      "insight",
      insight.id,
    );
    // An advisory has no Apply, so its thumbs ARE its terminal actions: 👍
    // acknowledges, 👎 dismisses — and it is the one place the UI captures why.
    trackInsightActionPressed(
      insightRef(insight),
      verdict === "helpful" ? "acknowledge" : "dismiss",
      verdict === "helpful" ? undefined : "not_helpful",
    );
    setBusyId(insight.id);
    setRateError(null);

    // 👍 acknowledges, and the card flips to its done style. 👎 dismisses, and a
    // dismissal lands on the click for both kinds — the card leaves the rail
    // before the calls resolve, and comes back with the error if either fails.
    const dismissing = verdict === "not_helpful";
    if (dismissing) setDismissed((prev) => [...prev, insight.id]);

    try {
      await client.submitFeedback(insight.id, verdict);
      if (verdict === "helpful") {
        const row = await client.acknowledge(insight.id);
        setDone((prev) => [
          ...prev.filter((d) => d.id !== row.id),
          { ...row, feedback_verdict: verdict },
        ]);
      } else {
        await client.dismiss(insight.id);
      }

      setVersion((v) => v + 1);
    } catch (err) {
      if (dismissing) {
        setDismissed((prev) => prev.filter((id) => id !== insight.id));
      }

      setRateError({ id: insight.id, message: friendlyError(err).message });
    } finally {
      setBusyId(null);
    }
  };

  if (insights.initialLoading) {
    return (
      <div className="mt-6">
        <div
          className="h-[110px] animate-pulse rounded-[5px]"
          style={{ background: "var(--surface-2)" }}
        />
      </div>
    );
  }

  return (
    <div className="mt-6">
      {/* The rail header counts the cards on screen (Figma 537:1042: the count
       * in the page's own text colour, the label muted). With nothing in the
       * rail the count is dropped rather than rendered as a "0". */}
      <p
        className="text-[15px] leading-none"
        style={{ color: "var(--text-muted)" }}
      >
        {cardCount > 0 && (
          <span style={{ color: "var(--text)" }}>{cardCount} </span>
        )}
        {cardCount > 0 ? "savings recommendations" : "Savings recommendations"}
      </p>
      <div className="mt-3 flex flex-col gap-[9px]">
        {/* `position` is the card's 0-based rank among the actionable cards —
         * the done cards below are not offers, so they are neither ranked nor
         * impression-tracked. */}
        {published.map((i, position) => (
          <FoundBanner
            key={i.id}
            insight={i}
            category={insightCategory(i, optimizations.data)}
            busy={busyId === i.id}
            error={rateError?.id === i.id ? rateError.message : null}
            onApply={() => transition(i, "apply", "Apply savings")}
            onDismiss={() => transition(i, "dismiss", "Dismiss")}
            onRate={(verdict) => rate(i, verdict)}
            onShown={() => trackInsightShown(insightRef(i), position)}
          />
        ))}
        {done.map((i) => (
          <DoneBanner
            key={i.id}
            insight={i}
            category={insightCategory(i, optimizations.data)}
          />
        ))}
        {error && (
          <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>
            {error}
          </p>
        )}
        {/* No insights — including a failed read (fail quiet to the nudge
         * card rather than surfacing an error in the rail). */}
        {published.length === 0 && done.length === 0 && <InfoBanner />}
      </div>
    </div>
  );
}

/* The cards' one accent (design token "Petal"), on the eyebrow label and on a
 * selected thumb. Hardcoded like the rest of the banner palette: these cards
 * keep a fixed dark ground in both themes, so they cannot take the page's. */
const BANNER_ACCENT = "#f4a2b9";

/* Shared banner scaffold: icon • lead column • detail column • divider • the
 * big ≈% stat • right-aligned actions, plus an optional `footer` row indented to
 * the lead column (29px padding + 32px icon + 40px gap = 101px) so a follow-up
 * control lines up with the card's own title instead of reading as a separate
 * strip. Column gaps and offsets mirror the Figma card (icon 29 / lead 99 /
 * detail 396). The detail column is the ONE flexible cell: it absorbs all slack
 * at wide viewports (content capped by the caller), so the divider + stat hug the
 * right-side actions like the design instead of stranding them mid-card —
 * especially with short insight copy. */
function BannerFrame({
  bg,
  icon,
  lead,
  detail,
  stat,
  actions,
  footer,
  floatDelay = 0.22,
  leadClassName = "w-[256px]",
}: {
  bg: string;
  icon: ReactNode;
  lead: ReactNode;
  detail: ReactNode;
  stat?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  floatDelay?: number;
  /** Width utility for the lead column. Defaults to the 256px design column;
   * widen it for banners whose lead is a full sentence rather than a label. */
  leadClassName?: string;
}) {
  // The ground arrives as a custom property rather than a `background`
  // declaration so the card's state stays legible in the DOM (jsdom drops a
  // `background: var(…)` shorthand outright; custom properties it keeps).
  return (
    <div
      className="tw-float-up rounded-[5px] bg-[var(--banner-bg)]"
      style={{ "--banner-bg": bg, "--float-delay": `${floatDelay}s` } as never}
    >
      <div
        className={`flex min-h-[110px] items-center gap-10 pl-[29px] pr-[25px] pt-4 ${
          footer ? "pb-2" : "pb-4"
        }`}
      >
        <div className="flex size-8 shrink-0 items-center justify-center">
          {icon}
        </div>
        <div className={`${leadClassName} shrink-0`}>{lead}</div>
        <div className="min-w-0 flex-1 text-[11px] leading-[1.5] tracking-[0.006em]">
          {detail}
        </div>
        {stat && (
          <div
            className="w-[124px] shrink-0 pl-[18px]"
            style={{ borderLeft: "1px solid rgba(153,136,142,0.5)" }}
          >
            {stat}
          </div>
        )}
        <div className="flex shrink-0 items-center gap-6">{actions}</div>
      </div>
      {footer && <div className="pb-4 pl-[101px] pr-[25px]">{footer}</div>}
    </div>
  );
}

/* The big savings figure. `value` is the insight's free-text
 * `projected_savings`, rendered verbatim (authors write e.g. "≈22%"). */
function BannerStat({ value, label }: { value: string; label: string }) {
  return (
    <>
      {/* Explicit colors: the DS base sheet paints bare <p> with --ai21-fg-1,
       * which goes dark-on-dark on this fixed-dark card in the light theme. */}
      <p
        className="tw-display text-[25px] leading-[1.2]"
        style={{ color: "#fbfbfb" }}
      >
        {value}
      </p>
      <p
        className="tw-mono mt-1 text-[10px] uppercase leading-none"
        style={{ color: "#99888e" }}
      >
        {label}
      </p>
    </>
  );
}

/* Category pill shared by the found/applied cards (Figma 351:5060). */
function CategoryChip({ category }: { category: string | null }) {
  if (!category) return null;

  return (
    // leading-[14px]: without it the chip inherits the card's line-height and stands twice as tall as its label.
    <span
      className="rounded-[10px] px-2 py-[2px] text-[10px] uppercase leading-[14px] tracking-[0.006em]"
      style={{
        border: "1px solid rgba(153,136,142,0.5)",
        color: "#99888e",
      }}
    >
      {category}
    </span>
  );
}

/* Card glyphs, traced from the design's exported vectors (Figma 537:595 bell /
 * 537:622 bulb / 351:5125 check). Each is a 40px disc centred on the frame's
 * 32px icon slot, so it overhangs the slot exactly as the design does. The
 * bell's counter is punched in the card's own ground rather than a fixed hex,
 * so the filled disc keeps reading as a cutout on either theme. */
function BellDisc() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      className="shrink-0"
    >
      <circle cx="20" cy="20" r="20" fill="#f4a2b9" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        fill="var(--banner-found-bg)"
        d="M19.5626 13.125C19.0448 13.125 18.6251 13.5447 18.6251 14.0625C18.6251 14.28 18.4996 14.478 18.303 14.571C16.7398 15.3102 15.7108 16.8497 15.6251 18.5761V20.8125C15.6251 20.8351 15.6237 20.8577 15.621 20.8801C15.5494 21.4712 15.3324 22.0259 14.9993 22.5H24.1258C23.7927 22.0259 23.5757 21.4712 23.5041 20.8801C23.5014 20.8577 23.5001 20.8351 23.5001 20.8125V18.5761C23.4143 16.8497 22.3853 15.3102 20.8221 14.571C20.6255 14.478 20.5001 14.28 20.5001 14.0625C20.5001 13.5447 20.0803 13.125 19.5626 13.125ZM21.2501 23.625H17.8751V23.8125C17.8751 24.7445 18.6306 25.5 19.5626 25.5C20.4945 25.5 21.2501 24.7445 21.2501 23.8125V23.625ZM16.7501 23.625V23.8125C16.7501 25.3658 18.0093 26.625 19.5626 26.625C21.1159 26.625 22.3751 25.3658 22.3751 23.8125V23.625H25.5626C25.818 23.625 26.0414 23.4528 26.1065 23.2058C26.1716 22.9588 26.062 22.6989 25.8397 22.573C25.1756 22.197 24.7266 21.532 24.6251 20.7768V18.5625C24.6251 18.5537 24.6248 18.5449 24.6244 18.5361C24.5288 16.498 23.3728 14.6678 21.5936 13.7014C21.4228 12.7345 20.5785 12 19.5626 12C18.5466 12 17.7023 12.7345 17.5315 13.7014C15.7523 14.6678 14.5963 16.498 14.5007 18.5361C14.5003 18.5449 14.5001 18.5537 14.5001 18.5625V20.7768C14.3985 21.532 13.9495 22.197 13.2854 22.573C13.0631 22.6989 12.9535 22.9588 13.0186 23.2058C13.0837 23.4528 13.3071 23.625 13.5626 23.625H16.7501Z"
      />
    </svg>
  );
}

function BulbDisc() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      className="shrink-0"
    >
      <circle cx="20" cy="20" r="20" fill="#f4f4f2" opacity="0.1" />
      <g
        stroke="#fbfbfb"
        strokeWidth="1.33333"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M22 21.3333C22.1333 20.6667 22.4667 20.2 23 19.6667C23.6667 19.0667 24 18.2 24 17.3333C24 16.2725 23.5786 15.2551 22.8284 14.5049C22.0783 13.7548 21.0609 13.3333 20 13.3333C18.9391 13.3333 17.9217 13.7548 17.1716 14.5049C16.4214 15.2551 16 16.2725 16 17.3333C16 18 16.1333 18.8 17 19.6667C17.4667 20.1333 17.8667 20.6667 18 21.3333" />
        <path d="M18 24H22" />
        <path d="M18.6667 26.6667H21.3333" />
      </g>
    </svg>
  );
}

function CheckDisc() {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 40 40"
      fill="none"
      className="shrink-0"
    >
      <circle cx="20" cy="20" r="20" fill="#f4f4f2" opacity="0.1" />
      {/* The design places the exported 16px check glyph at 1.25x, offset to
       * the disc's optical centre. */}
      <g transform="translate(10 10.625) scale(1.25)">
        <path
          d="M13.3328 4L6.0002 11.3328L2.6672 7.99971"
          stroke="#fbfbfb"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
}

/* Thumb glyphs, traced from the design's exported vectors (Figma 537:1053 up /
 * 537:1055 down). 16px glyphs in the design's 24px hit area. */
function ThumbIcon({ down = false }: { down?: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    >
      {down ? (
        <path d="M11.3337 9.33344V1.3328M6.00001 12.0803L6.66672 9.33344H2.77981C2.57281 9.33344 2.36864 9.28524 2.18349 9.19267C1.99834 9.10009 1.83729 8.96567 1.71308 8.80006C1.58888 8.63446 1.50494 8.4422 1.46791 8.23853C1.43088 8.03486 1.44177 7.82537 1.49974 7.62664L3.05316 2.29288C3.13395 2.0159 3.30238 1.7726 3.53319 1.59949C3.764 1.42638 4.04473 1.3328 4.33324 1.3328H13.3338C13.6874 1.3328 14.0266 1.47329 14.2767 1.72336C14.5267 1.97342 14.6672 2.31259 14.6672 2.66624V8C14.6672 8.35365 14.5267 8.69282 14.2767 8.94288C14.0266 9.19295 13.6874 9.33344 13.3338 9.33344H11.4937C11.2456 9.33357 11.0025 9.4029 10.7917 9.53364C10.5808 9.66438 10.4107 9.85134 10.3003 10.0735L8.00013 14.6672C7.68573 14.6633 7.37627 14.5884 7.09487 14.4481C6.81348 14.3078 6.56742 14.1058 6.37509 13.857C6.18277 13.6082 6.04913 13.3193 5.98418 13.0116C5.91923 12.7039 5.92464 12.3856 6.00001 12.0803Z" />
      ) : (
        <path d="M4.66633 6.66656V14.6672M9.99999 3.91967L9.33328 6.66656H13.2202C13.4272 6.66656 13.6314 6.71476 13.8165 6.80734C14.0017 6.89991 14.1627 7.03433 14.2869 7.19994C14.4111 7.36554 14.4951 7.5578 14.5321 7.76147C14.5691 7.96514 14.5582 8.17463 14.5003 8.37336L12.9468 13.7071C12.8661 13.9841 12.6976 14.2274 12.4668 14.4005C12.236 14.5736 11.9553 14.6672 11.6668 14.6672H2.66621C2.31257 14.6672 1.97341 14.5267 1.72335 14.2766C1.47328 14.0266 1.3328 13.6874 1.3328 13.3338V8C1.3328 7.64635 1.47328 7.30718 1.72335 7.05712C1.97341 6.80705 2.31257 6.66656 2.66621 6.66656H4.50633C4.7544 6.66643 4.99751 6.5971 5.20834 6.46636C5.41916 6.33562 5.58934 6.14866 5.69973 5.9265L7.99987 1.3328C8.31427 1.33669 8.62373 1.41159 8.90513 1.55188C9.18652 1.69218 9.43258 1.89425 9.6249 2.143C9.81723 2.39175 9.95087 2.68075 10.0158 2.9884C10.0808 3.29605 10.0754 3.61441 9.99999 3.91967Z" />
      )}
    </svg>
  );
}

function ThumbButton({
  label,
  down = false,
  selected,
  disabled,
  onClick,
}: {
  label: string;
  down?: boolean;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={selected}
      className="flex size-6 items-center justify-center transition-opacity hover:opacity-100 disabled:opacity-30"
      style={{
        color: selected ? BANNER_ACCENT : "#99888e",
        opacity: selected ? 1 : 0.7,
      }}
      disabled={disabled}
      onClick={onClick}
    >
      <ThumbIcon down={down} />
    </button>
  );
}

/* The actionable insight card, one style per kind: "Optimization found" on the
 * near-black ground (Figma 537:592) vs. "Recommended action" on the lighter
 * maroon (Figma 537:621). A config_change insight keeps the Dismiss + "Apply
 * savings" pair — those already say whether it was useful, so it carries no
 * thumbs. An advisory insight has nothing to apply: its 👍/👎 are the only
 * controls, and a click commits both the verdict and the lifecycle exit it
 * implies. The card leaves the rail on success, so the only state it holds is
 * the failure the footer reports. */
function FoundBanner({
  insight,
  category,
  busy,
  error,
  onApply,
  onDismiss,
  onRate,
  onShown,
}: {
  insight: Insight;
  category: string | null;
  busy: boolean;
  error: string | null;
  onApply: () => void;
  onDismiss: () => void;
  onRate: (verdict: FeedbackVerdict) => void;
  /** Fired once the card is actually on screen — see `useOnVisible`. */
  onShown: () => void;
}) {
  const advisory = insightKind(insight) === "advisory";
  const verdict = insight.feedback_verdict;
  const shownRef = useOnVisible(onShown);

  return (
    <div ref={shownRef} style={{ color: "#fbfbfb" }}>
      <BannerFrame
        bg={advisory ? "var(--banner-advisory-bg)" : "var(--banner-found-bg)"}
        icon={advisory ? <BulbDisc /> : <BellDisc />}
        lead={
          <>
            <div className="flex items-center gap-2">
              <span
                className="tw-mono text-[11px] uppercase tracking-[0.01em]"
                style={{ color: BANNER_ACCENT }}
              >
                {advisory ? "Recommended action" : "Optimization found"}
              </span>
              <CategoryChip category={category} />
            </div>
            <p
              className="mt-2 text-[15px] font-medium leading-[20px]"
              style={{ color: "#fbfbfb" }}
            >
              {insight.title}
            </p>
          </>
        }
        detail={<p style={{ color: "#99888e" }}>{insight.narrative}</p>}
        stat={
          insight.projected_savings ? (
            <BannerStat
              value={insight.projected_savings}
              label="Potential savings"
            />
          ) : undefined
        }
        actions={
          advisory ? (
            <>
              <span className="text-[11px]" style={{ color: "#99888e" }}>
                Was this useful?
              </span>
              <ThumbButton
                label="Helpful"
                selected={verdict === "helpful"}
                disabled={busy}
                onClick={() => onRate("helpful")}
              />
              <ThumbButton
                label="Not helpful"
                down
                selected={verdict === "not_helpful"}
                disabled={busy}
                onClick={() => onRate("not_helpful")}
              />
            </>
          ) : (
            <>
              <button
                className="text-[11px] opacity-50 transition-opacity hover:opacity-80 disabled:opacity-30"
                style={{ color: "#99888e" }}
                disabled={busy}
                onClick={onDismiss}
              >
                Dismiss
              </button>
              <button
                className="rounded-[2px] px-[15px] py-[9px] text-[13px] transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{
                  background: "rgba(231,33,100,0.2)",
                  border: "1px solid rgba(231,33,100,0.5)",
                  color: "#fbfbfb",
                }}
                disabled={busy}
                onClick={onApply}
              >
                Apply savings
              </button>
            </>
          )
        }
        /* A failed rating belongs on the card whose thumb produced it, indented
         * to its title column so it reads as part of the card. */
        footer={
          error ? (
            <p className="text-[11px]" style={{ color: "#e99daa" }}>
              {error}
            </p>
          ) : undefined
        }
      />
    </div>
  );
}

/* Card label per terminal state. Dismissal never lands here — a dismissed
 * insight leaves the rail outright, for both kinds. */
const DONE_LABELS: Partial<Record<InsightState, string>> = {
  applied: "Optimization applied",
  acknowledged: "Marked as done",
};

/* The confirmation card an actionable insight flips to (Figma 351:5123), keyed
 * off the state the transition returned — so both kinds land here: a
 * config_change insight on Apply, an advisory one on 👍. Its own desaturated
 * ground, one step off both found styles, plus a check glyph for the disc; the
 * label sits alone in the lead column and the chip + title move to the wide
 * column. Purely terminal: every input, feedback included, was taken before the
 * transition, because this card cannot survive a refresh. */
function DoneBanner({
  insight,
  category,
}: {
  insight: Insight;
  category: string | null;
}) {
  return (
    <div style={{ color: "#fbfbfb" }}>
      <BannerFrame
        bg="var(--banner-applied-bg)"
        icon={<CheckDisc />}
        lead={
          <span
            className="tw-mono text-[11px] uppercase tracking-[0.01em]"
            style={{ color: BANNER_ACCENT }}
          >
            {DONE_LABELS[insight.state] ?? "Optimization applied"}
          </span>
        }
        detail={
          <div className="max-w-[437px]">
            <CategoryChip category={category} />
            <p
              className="mt-2 text-[15px] font-medium leading-[20px]"
              style={{ color: "#fbfbfb" }}
            >
              {insight.title}
            </p>
          </div>
        }
      />
    </div>
  );
}

/* Empty state (Figma 351:5287): a light bordered card shown while the agent
 * has no insights — nudge toward sending more traffic. The icon is the
 * design's dot-lattice sphere (Figma "Group 7") inlined like the other banner
 * glyphs. */
function InfoBanner() {
  return (
    <div
      className="rounded-[5px]"
      style={{
        border: "1px solid var(--banner-info-border)",
        color: "var(--text-muted)",
      }}
    >
      <BannerFrame
        bg="var(--banner-info-bg)"
        floatDelay={0.28}
        leadClassName="w-[380px]"
        icon={
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
            <g fill="#d3d0d1" stroke="none">
              <circle cx="2" cy="2" r="2" />
              <circle cx="2" cy="16" r="2" />
              <circle cx="2" cy="30" r="2" />
              <circle cx="16" cy="2" r="2" />
              <circle cx="16" cy="16" r="2" />
              <circle cx="16" cy="30" r="2" />
              <circle cx="30" cy="2" r="2" />
              <circle cx="30" cy="16" r="2" />
              <circle cx="30" cy="30" r="2" />
            </g>
            <g stroke="#d3d0d1" fill="none">
              <path d="M2 2L29.5 30.5" />
              <path d="M1.5 30L30 2.5" />
              <path d="M2 16L16 2.5L30 16L16 30L2 16Z" />
            </g>
          </svg>
        }
        lead={
          <p
            className="text-[15px] font-medium leading-[20px]"
            style={{ color: "var(--text-muted)" }}
          >
            For more savings recommendations, keep using your agent as usual.
          </p>
        }
        detail={
          // mx-auto + w-fit: the empty state carries no stat or actions, so the
          // line is centred in that slack instead of hugging the lead column.
          <p
            className="mx-auto w-fit max-w-[606px] pl-10 leading-[16.5px]"
            style={{ color: "var(--text-muted)" }}
          >
            New ways to save will appear here as we find them.
          </p>
        }
      />
    </div>
  );
}

// ── Agent configuration ──────────────────────────────────────────────────────
// The design's full-bleed configuration block (App 284:3459): a 41px header,
// the family jump-nav on the left, and per-family sections of optimization
// cards. Driven by the real transformer schema (src/configs) + the per-agent
// discovery endpoint, and persisted to the agent's bound config profile.
//
// Seeded from the agent's bound profile (config_id → its `overrides`), overlaid
// on the gateway-resolved schema defaults. Toggles update local state
// optimistically, then a debounced write persists the whole overrides payload
// in one PATCH — so a burst of clicks coalesces into a single request. While a
// write is in flight the toggles are disabled; a failed write reverts to the
// last-saved values. If the agent has no profile yet, the first write creates
// one (named after the agent) and binds it via agents.update(config_id).

/** How long to wait after the last toggle before writing (coalesces bursts). */
const SAVE_DEBOUNCE_MS = 600;

/** Distance below the scroller's top edge where a family section becomes the
 *  active one (and, halved, where a nav click parks it). */
const SPY_TOP_OFFSET = 32;

/** The scrolling ancestor that owns the page scroll (the shell's `<main>`). */
function scrollParent(el: HTMLElement | null): HTMLElement | null {
  let sc = el?.parentElement ?? null;
  while (sc && !/(auto|scroll)/.test(getComputedStyle(sc).overflowY)) {
    sc = sc.parentElement;
  }
  return sc;
}

type SaveState = "idle" | "saving" | "error";

function AgentConfiguration({
  agentId,
  configId,
  agentName,
  canEdit,
  onBind,
}: {
  agentId: string;
  configId: string | null;
  agentName: string;
  /** Whether the signed-in caller may write to this agent's config. */
  canEdit: boolean;
  onBind: () => void;
}) {
  const configClient = useConfigClient();
  const agentClient = useAgentClient();
  const { schema, ready } = useConfigSchema();
  // The backend discovery list is the source of truth for WHICH optimizations
  // exist for this agent, their order/grouping, labels, defaults, and coming-soon
  // state (OPSS-152). Local TRANSFORMERS specs are joined by id only for the
  // presentation the backend doesn't model (glyph, safety) + the field controls.
  const optimizations = useAgentOptimizations(agentId);
  // Re-key the profile read after we create/mutate it, so it reflects the write.
  const [configVersion, setConfigVersion] = useState(0);
  const profile = useConfig(configId ?? "", configVersion);

  const groups = useMemo(() => allGroups(schema), [schema]);

  // The groups we're allowed to PERSIST: the gateway master switch plus only
  // the settings groups the backend exposes for this agent (the discovery-list
  // ids). The full static catalog (`groups`) still seeds the local value map,
  // so this is a drift guard: `schema.ts` is a hand-maintained mirror, and any
  // group it lists that the backend no longer treats as overridable would be
  // rejected on write → a 400. Keys absent from discovery are dropped instead.
  // Dropping is silent, so a group meant to be user-editable must be added to
  // the backend catalog too, or its card will save as a no-op.
  const saveGroups = useMemo(() => {
    const exposed = new Set((optimizations.data ?? []).flatMap((o) => o.keys));
    return groups.filter((g) => g.key === "gateway" || exposed.has(g.key));
  }, [groups, optimizations.data]);

  // Seed once the schema is ready and the bound profile (if any) has loaded.
  const [values, setValues] = useState<ConfigValues | null>(null);
  const savedRef = useRef<ConfigValues | null>(null);
  const boundIdRef = useRef<string | null>(configId);
  const profileLoaded = configId ? profile.data !== undefined : true;

  useEffect(() => {
    if (values !== null || !ready || !profileLoaded) return;

    const seeded =
      configId && profile.data
        ? valuesFromOverrides(profile.data.overrides, groups)
        : defaultValues(groups);
    setValues(seeded);
    savedRef.current = seeded;
    boundIdRef.current = configId;
  }, [values, ready, profileLoaded, configId, profile.data, groups]);

  const [saveState, setSaveState] = useState<SaveState>("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The values a scheduled-but-not-yet-sent write would persist, so unmount can
  // flush them instead of dropping them.
  const queuedRef = useRef<ConfigValues | null>(null);
  const mountedRef = useRef(true);

  /* The write itself, with no state updates of its own — callable both from the
   * debounce timer (which wraps it in save state) and from unmount, where
   * touching state is pointless. */
  const writeOverrides = async (next: ConfigValues) => {
    const overrides = overridesFromValues(next, saveGroups);
    const boundId = boundIdRef.current;
    if (boundId) {
      await configClient.update(boundId, { overrides });
    } else {
      // No profile yet — create one named after the agent and bind it.
      const created = await configClient.create({
        config_id: agentId,
        name: `${agentName} config`,
        overrides,
      });
      await agentClient.update(agentId, { config_id: created.config_id });
      boundIdRef.current = created.config_id;
      if (mountedRef.current) {
        setConfigVersion((v) => v + 1);
        onBind();
      }
    }

    // The profile read is cached process-wide, and `configVersion` restarts at 0
    // on every mount — so without dropping the entry, coming back to this page
    // re-seeds the editor from the pre-write overrides and the toggle the reader
    // just flipped springs back (OPSU-16).
    if (boundIdRef.current) {
      invalidateQueries(`${workspaceKeyPrefix()}config:${boundIdRef.current}`);
    }
  };

  // Unmount must flush through the CURRENT write path: a cleanup that closed
  // over the first render's would carry that render's empty `saveGroups` (the
  // discovery list hadn't landed yet) and PUT a payload stripped of every group
  // but `gateway`, erasing the rest of the profile.
  const writeRef = useRef(writeOverrides);
  writeRef.current = writeOverrides;

  useEffect(
    () => () => {
      mountedRef.current = false;
      // A queued value only survives until the timer fires, so this is exactly
      // the "scheduled but never sent" case — leaving the page inside the
      // debounce window used to discard it, losing the toggle the reader had
      // just flipped (OPSU-16). Send it now instead. Nothing is left to render,
      // so a failure can only be swallowed; the next mount reseeds from the
      // server either way.
      const queued = queuedRef.current;
      if (!queued) return;

      if (timer.current) clearTimeout(timer.current);

      // No UI is left to show an error in, so report the failure to telemetry
      // rather than dropping it on the floor.
      void writeRef.current(queued).catch((err: unknown) => {
        trackApiFailed("saveAgentConfig", err);
      });
    },
    [],
  );

  // Debounced write-behind. Persists `next` (the caller's optimistic value map)
  // to the bound profile, creating + binding one if the agent has none yet.
  const scheduleSave = (next: ConfigValues) => {
    if (timer.current) clearTimeout(timer.current);

    queuedRef.current = next;
    timer.current = setTimeout(async () => {
      // In flight from here on: clear the queue so an unmount mid-request
      // doesn't send the same payload a second time.
      queuedRef.current = null;
      timer.current = null;
      setSaveState("saving");
      try {
        await writeOverrides(next);
        savedRef.current = next;
        setSaveState("idle");
      } catch {
        // Revert to the last persisted values so the UI matches the backend.
        if (savedRef.current) setValues(savedRef.current);

        setSaveState("error");
      }
    }, SAVE_DEBOUNCE_MS);
  };

  const commit = (compute: (v: ConfigValues) => ConfigValues) => {
    // Disabled while a write is in flight, not yet seeded, or the caller may
    // not write to this agent — ignore toggles in any of those states.
    if (saveState === "saving" || values === null || !canEdit) return;

    const next = compute(values);
    setValues(next);
    scheduleSave(next);
  };

  // The optimizations the backend returned for this agent, grouped into the
  // visible families in FAMILIES order. Only `available` items are toggleable;
  // `coming_soon` render as inert teasers and never count toward enablement.
  const sections = useMemo(() => {
    const list = optimizations.data ?? [];
    return FAMILIES.map((family) => ({
      family,
      items: list.filter((o) => o.category === family.key),
    })).filter((s) => s.items.length > 0);
  }, [optimizations.data]);

  // The settings-group keys a family's available cards toggle. A card may drive
  // several groups (e.g. tool_result_compression = rtk + local_compression);
  // they flip together, so the whole set is what we enable/count.
  const toggleableKeys = (items: AgentOptimization[]) =>
    items
      .filter((o) => o.availability === "available")
      .flatMap((o) => o.keys.filter((k) => transformerByKey(k)));

  const countOn = (keys: string[], v: ConfigValues) =>
    keys.filter((k) => v[k]?.enabled === true).length;

  // Enablement is counted in CARDS: a multi-key card (e.g. compression = rtk +
  // local_compression) is one item, "on" only when all its keys are on. The
  // design's "1/4" denominator counts every card, coming-soon teasers included.
  const availableCards = (items: AgentOptimization[]) =>
    items.filter((o) => o.availability === "available");

  const cardOn = (opt: AgentOptimization, v: ConfigValues) =>
    opt.keys.length > 0 && opt.keys.every((k) => v[k]?.enabled === true);

  const countCardsOn = (items: AgentOptimization[], v: ConfigValues) =>
    availableCards(items).filter((o) => cardOn(o, v)).length;

  const toggleFamily = (items: AgentOptimization[]) =>
    commit((v) => {
      const keys = toggleableKeys(items);
      const allOn = keys.length > 0 && countOn(keys, v) === keys.length;
      // Fold every toggleable key in the family to the same target state,
      // honoring the gateway's mutual-exclusivity rule per key.
      return keys.reduce(
        (acc, k) =>
          applyExclusivity({ ...acc, [k]: { ...acc[k], enabled: !allOn } }, k),
        v,
      );
    });

  // Flip all of a card's keys to the same target (on unless every key is on).
  const toggleOptimization = (opt: AgentOptimization) =>
    commit((v) => {
      const enabled = !opt.keys.every((k) => v[k]?.enabled === true);
      trackButtonPressed(
        enabled ? "Enable optimization" : "Disable optimization",
        opt.id,
      );
      return opt.keys.reduce(
        (acc, k) =>
          applyExclusivity({ ...acc, [k]: { ...acc[k], enabled } }, k),
        v,
      );
    });

  const familyRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [activeFamily, setActiveFamily] = useState<string | null>(null);
  /** Set while a nav click owns the highlight; any reader gesture releases it. */
  const pinnedRef = useRef(false);

  const jumpToFamily = (family: string) => {
    setActiveFamily(family);
    // A trailing family can sit past the last scrollable position, so the jump
    // cannot park it at the edge; pin it until the reader scrolls by hand,
    // otherwise the spy would immediately overrule their click.
    pinnedRef.current = true;
    const el = familyRefs.current[family];
    if (!el) return;

    const sc = scrollParent(el);
    if (sc) {
      const r = el.getBoundingClientRect();
      const sr = sc.getBoundingClientRect();
      sc.scrollTop += r.top - sr.top - SPY_TOP_OFFSET / 2;
    }
  };

  // Scroll-spy: the nav highlight follows the section crossing the scroller's
  // top edge, so hand-scrolling shows where the reader is (a nav click keeps
  // ownership via `pinnedRef` until the next gesture).
  //
  // `sectionsReady` is a real dependency, not a guard: the family list resolves
  // with the discovery read, but the sections -- and so the refs the spy
  // measures -- only mount once the config values land too.
  const sectionsReady = values !== null && optimizations.data !== undefined;
  useEffect(() => {
    const keys = sections.map((s) => s.family.key);
    const sc = sectionsReady
      ? scrollParent(familyRefs.current[keys[0]] ?? null)
      : null;
    if (!sc || keys.length === 0) return;

    // One rect per family, and React bails out when the key is unchanged, so
    // this is cheap enough to run straight off the scroll event.
    const sync = () => {
      if (pinnedRef.current) return;

      // The probe line glides from the scroller's top edge (scroll start) to its
      // bottom (scroll end). A fixed top-edge probe would skip every trailing
      // section the content is too short to pull up that far -- here they each
      // still get a share of the scroll.
      const max = Math.max(0, sc.scrollHeight - sc.clientHeight);
      const box = sc.getBoundingClientRect();
      const travel = Math.max(0, sc.clientHeight - SPY_TOP_OFFSET);
      const progress = max > 0 ? sc.scrollTop / max : 0;
      const probe = box.top + SPY_TOP_OFFSET + progress * travel;

      // Above the first section nothing has crossed yet, so it leads.
      let current = keys[0];
      for (const key of keys) {
        const el = familyRefs.current[key];
        if (el && el.getBoundingClientRect().top <= probe) current = key;
      }
      setActiveFamily(current);
    };

    // Hand-scrolling (not the programmatic jump) is what releases a pin, so
    // the gestures are listened for separately from the scroll itself.
    const release = () => {
      pinnedRef.current = false;
    };

    sync();
    sc.addEventListener("scroll", sync, { passive: true });
    sc.addEventListener("wheel", release, { passive: true });
    sc.addEventListener("touchmove", release, { passive: true });
    window.addEventListener("keydown", release);
    window.addEventListener("resize", sync);
    return () => {
      sc.removeEventListener("scroll", sync);
      sc.removeEventListener("wheel", release);
      sc.removeEventListener("touchmove", release);
      window.removeEventListener("keydown", release);
      window.removeEventListener("resize", sync);
    };
  }, [sections, sectionsReady]);

  // Toggles are inert while a write is in flight (debounced auto-save) and until
  // the initial seed resolves.
  const disabled = saveState === "saving" || values === null || !canEdit;
  // Only surface the permission reason specifically — a control disabled for
  // some other reason (saving, still seeding) needs no explanation on hover.
  const permissionReason = editDisabledReason({ can_edit: canEdit });

  // Full-bleed against the shell's main padding (px-5 py-4), so the block's
  // ground and hairlines run edge to edge like the design's App frame.
  const bleed = "-mx-5 -mb-8 mt-6";

  if (optimizations.error) {
    return (
      <div className={bleed} style={{ background: "var(--config-bg)" }}>
        <ConfigHeader />
        <p className="px-6 py-5 text-[12px] text-[var(--text-muted)]">
          {friendlyError(optimizations.error).message}
        </p>
      </div>
    );
  }

  // Wait for both the backend discovery list and the seeded config values.
  if (values === null || optimizations.data === undefined) {
    return (
      <div className={bleed} style={{ background: "var(--config-bg)" }}>
        <ConfigHeader />
        <div className="m-6 h-40 animate-pulse bg-[var(--surface-2)]" />
      </div>
    );
  }

  const active = activeFamily ?? sections[0]?.family.key;

  return (
    <div
      className={`tw-float-up ${bleed}`}
      style={
        { background: "var(--config-bg)", "--float-delay": "0.34s" } as never
      }
    >
      <ConfigHeader>
        {saveState === "saving" && (
          <span className="text-[11px] text-[var(--text-muted)]">Saving…</span>
        )}
        {saveState === "error" && (
          <span className="text-[11px]" style={{ color: "var(--val-crimson)" }}>
            Couldn&apos;t save — reverted
          </span>
        )}
      </ConfigHeader>

      <div className="flex items-stretch">
        {/* Family jump-nav (only families the backend returned) */}
        <div
          className="w-[176px] flex-shrink-0 px-3 py-[21px]"
          style={{ borderRight: "1px solid var(--config-border)" }}
        >
          <div className="sticky top-4 flex flex-col gap-[7px]">
            {sections.map(({ family }) => (
              <button
                key={family.key}
                onClick={() => jumpToFamily(family.key)}
                className="flex w-full items-center rounded-[5px] px-3 py-1.5 text-left"
              >
                <span
                  className={`tw-mono text-[12px] capitalize leading-[16px] ${
                    family.key === active ? "font-medium underline" : ""
                  }`}
                  style={{
                    color:
                      family.key === active
                        ? "var(--text)"
                        : "var(--text-muted)",
                  }}
                >
                  {family.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Sections — every family, all cards visible */}
        <div
          className="min-w-0 flex-1 transition-opacity"
          style={{ opacity: saveState === "saving" ? 0.6 : 1 }}
        >
          {sections.map(({ family, items }) => {
            const on = countCardsOn(items, values);
            return (
              <div
                key={family.key}
                ref={(el) => {
                  familyRefs.current[family.key] = el;
                }}
                className="mx-8 scroll-mt-4 pb-[33px] pt-8"
                style={{ borderBottom: "1px solid var(--config-border)" }}
              >
                <div className="flex items-start justify-between gap-3">
                  <h3
                    className="tw-mono text-[15px] uppercase leading-none tracking-[0.03em]"
                    style={{ color: "var(--text)" }}
                  >
                    {family.label}
                  </h3>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <span className="tw-mono text-[12px] leading-[16px] tabular-nums text-[var(--text-muted)]">
                      {on}/{items.length}
                    </span>
                    <Toggle
                      on={
                        availableCards(items).length > 0 &&
                        on === availableCards(items).length
                      }
                      onClick={() => toggleFamily(items)}
                      label={`Enable all ${family.label}`}
                      disabled={disabled || availableCards(items).length === 0}
                      disabledReason={permissionReason}
                    />
                  </div>
                </div>
                <p className="mt-1 text-[12px] leading-4 text-[var(--text-muted)]">
                  {family.blurb}
                </p>
                <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((opt) => {
                    const comingSoon = opt.availability === "coming_soon";
                    // Every key must resolve to a local config group to be
                    // persistable; a join miss (FE lagging the backend) renders
                    // an informational, non-toggleable teaser.
                    const toggleable =
                      !comingSoon &&
                      opt.keys.length > 0 &&
                      opt.keys.every((k) => transformerByKey(k));
                    return (
                      <OptimizationCard
                        key={opt.id}
                        opt={opt}
                        on={
                          toggleable &&
                          opt.keys.every((k) => values[k]?.enabled === true)
                        }
                        onToggle={() => toggleOptimization(opt)}
                        disabled={disabled || !toggleable}
                        disabledReason={permissionReason}
                        comingSoon={comingSoon}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* Shown in place of the configuration block for a traffic-only agent (tagged
 * via x-agent-id but never registered, so the settings reads 404). Offers
 * registration as a deliberate action rather than a side effect of viewing
 * the page — without it the block dead-ends on "agent_id not found". */
function RegisterAgentPanel({
  onRegister,
}: {
  onRegister: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (busy) return;

    setBusy(true);
    setError(null);
    try {
      await onRegister();
      // On success the parent re-reads the agent and swaps this panel for the
      // real configuration block — leave `busy` on so the button can't refire.
    } catch (err) {
      setError(friendlyError(err).message);
      setBusy(false);
    }
  };

  return (
    <div
      className="tw-float-up -mx-5 -mb-8 mt-6"
      style={{ background: "var(--config-bg)" }}
    >
      <ConfigHeader />
      <div className="flex flex-col items-start gap-3 px-6 py-6">
        <p className="text-[13px] leading-5 text-[var(--text-muted)]">
          This agent is sending traffic but hasn&apos;t been set up yet. Set it
          up to edit its name and configure its optimizations.
        </p>
        <button
          onClick={() => {
            trackButtonPressed("Set up agent", "agent");
            void submit();
          }}
          disabled={busy}
          className="rounded-[5px] px-4 py-2 text-[13px] font-semibold disabled:opacity-50"
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            color: "var(--text)",
          }}
        >
          {busy ? "Setting up…" : "Set up agent"}
        </button>
        {error && (
          <p className="text-[11px]" style={{ color: "var(--val-crimson)" }}>
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

/* Block header (Figma Header 284:3460): the 41px "Agent configuration" bar. */
function ConfigHeader({ children }: { children?: ReactNode }) {
  return (
    <div
      className="flex h-[41px] items-center justify-between px-6"
      style={{ borderBottom: "1px solid var(--config-border)" }}
    >
      <p className="text-[15px] leading-none" style={{ color: "var(--text)" }}>
        Agent configuration
      </p>
      <div className="flex items-center gap-3">{children}</div>
    </div>
  );
}

/* Safety chip palette (Badge 284:3505): fixed in both themes. */
const SAFETY_STYLE: Record<Safety, { bg: string; color: string }> = {
  lossless: { bg: "#c4e8d7", color: "#416d69" },
  lossy: { bg: "#f3d5dd", color: "#991d47" },
  routing: { bg: "var(--tint-blue)", color: "var(--val-blue)" },
  experimental: { bg: "var(--tint-amber)", color: "var(--val-amber)" },
};

/* One optimization card (OptCard 284:3488). The backend `opt` is the source of
 * truth for label/description/availability/safety and the settings groups it
 * toggles; an enabled card raises onto its own ground and inverts its tile. */
function OptimizationCard({
  opt,
  on,
  onToggle,
  disabled,
  disabledReason,
  comingSoon,
}: {
  opt: AgentOptimization;
  on: boolean;
  onToggle: () => void;
  disabled?: boolean;
  disabledReason?: string;
  comingSoon?: boolean;
}) {
  const safety = {
    ...SAFETY_STYLE[opt.safety],
    label: SAFETY_LABEL[opt.safety],
  };
  return (
    <div
      className="flex min-h-[147px] flex-col gap-3 rounded-[5px] p-[17px]"
      style={{
        background: on ? "var(--opt-card-on)" : "transparent",
        border: "1px solid var(--config-border)",
        transition: "background 150ms ease",
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div
          className="flex size-10 flex-shrink-0 items-center justify-center rounded-[5px]"
          style={{
            background: on ? "var(--opt-tile-on-bg)" : "var(--opt-tile-off-bg)",
            color: on ? "var(--opt-tile-on-fg)" : "var(--opt-tile-off-fg)",
            transition: "background 150ms ease, color 150ms ease",
          }}
        >
          <OptimizationIcon id={opt.id} family={opt.category} />
        </div>
        {/* No toggle for a coming-soon teaser — the mono tag stands in for it,
            keeping the header row aligned with the live cards. */}
        {comingSoon ? (
          <span className="tw-mono pt-1 text-[10px] uppercase leading-[15px] tracking-[0.025em] text-[var(--text-muted)]">
            Coming soon
          </span>
        ) : (
          <Toggle
            on={on}
            onClick={onToggle}
            label={`Enable ${opt.name}`}
            disabled={disabled}
            disabledReason={disabledReason}
          />
        )}
      </div>
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <p
            className="text-[14px] font-medium leading-5"
            style={{ color: "var(--text)" }}
          >
            {opt.name}
          </p>
          {!comingSoon && (
            <span
              className="tw-mono rounded-[3px] px-1.5 py-0.5 text-[10px] uppercase leading-[15px] tracking-[0.025em]"
              style={{ background: safety.bg, color: safety.color }}
            >
              {safety.label}
            </span>
          )}
        </div>
        <p className="mt-1 max-w-[265px] text-[12px] leading-[1.6] text-[var(--text-muted)]">
          {opt.description}
        </p>
      </div>
    </div>
  );
}

/* Pill toggle (Toggle 284:3481): 36×20 track, 16px knob. */
function Toggle({
  on,
  onClick,
  label,
  disabled,
  disabledReason,
}: {
  on: boolean;
  onClick: () => void;
  label: string;
  disabled?: boolean;
  /** Shown on hover — only pass this when `disabled` is specifically a
   *  permission verdict, not e.g. a save in flight. */
  disabledReason?: string;
}) {
  const toggle = (
    <button
      onClick={onClick}
      disabled={disabled}
      role="switch"
      aria-checked={on}
      aria-label={label}
      className="relative h-5 w-9 flex-shrink-0 rounded-full transition-colors disabled:cursor-not-allowed"
      style={{ background: on ? "var(--toggle-on)" : "var(--toggle-off)" }}
    >
      <span
        className="absolute size-4 rounded-full bg-white transition-all"
        style={{
          top: "50%",
          transform: "translateY(-50%)",
          left: on ? "18px" : "2px",
        }}
      />
    </button>
  );

  if (disabled && disabledReason) {
    return <Tooltip content={disabledReason}>{toggle}</Tooltip>;
  }

  return toggle;
}
